import { useEffect, useRef } from 'react';
import { isLowEnd, isMidEnd } from '../lib/usePerf';

/**
 * FuturisticBG — animated orange/white lines that avoid cursor
 * OPTIMIZED: pauses when off-screen, reduces density for low-end devices
 */
export const FuturisticBG = ({ density = 14 }) => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: -9999, y: -9999 });
  const touch = useRef({ x: -9999, y: -9999 });

  // Adjust density based on device tier
  const actualDensity = isLowEnd ? 0 : isMidEnd ? Math.min(density, 6) : density;

  useEffect(() => {
    // Skip entirely on low-end devices
    if (actualDensity === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let isVisible = false;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // IntersectionObserver — only animate when visible
    const io = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible && !animId) draw();
      },
      { threshold: 0, rootMargin: '100px' }
    );
    io.observe(canvas);

    // Line objects
    const lines = Array.from({ length: actualDensity }, (_, i) => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      len: 60 + Math.random() * 110,
      angle: Math.random() * Math.PI * 2,
      va: (Math.random() - 0.5) * 0.006,
      opacity: 0.06 + Math.random() * 0.10,
      color: i % 3 === 0 ? '#f97316' : i % 3 === 1 ? '#fbbf24' : '#ffffff',
      width: 0.8 + Math.random() * 0.8,
    }));

    // Pointer tracking
    const onMove = e => {
      if (!isVisible) return;
      const rect = canvas.getBoundingClientRect();
      mouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onTouch = e => {
      if (!isVisible) return;
      const t = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      touch.current = { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onTouch, { passive: true });

    const draw = () => {
      if (!isVisible) {
        animId = null;
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cursor = mouse.current.x > 0 ? mouse.current : touch.current;

      lines.forEach(l => {
        l.x += l.vx;
        l.y += l.vy;
        l.angle += l.va;

        // Cursor repulsion
        const dx = l.x - cursor.x;
        const dy = l.y - cursor.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120 && dist > 0) {
          const push = ((120 - dist) / 120) * 2.4;
          l.x += (dx / dist) * push;
          l.y += (dy / dist) * push;
        }

        // Wrap
        const W = canvas.width, H = canvas.height;
        if (l.x < -l.len) l.x = W + l.len;
        if (l.x > W + l.len) l.x = -l.len;
        if (l.y < -l.len) l.y = H + l.len;
        if (l.y > H + l.len) l.y = -l.len;

        // Draw
        const ex = l.x + Math.cos(l.angle) * l.len;
        const ey = l.y + Math.sin(l.angle) * l.len;

        const grad = ctx.createLinearGradient(l.x, l.y, ex, ey);
        grad.addColorStop(0, 'transparent');
        grad.addColorStop(0.4, l.color);
        grad.addColorStop(0.6, l.color);
        grad.addColorStop(1, 'transparent');

        ctx.beginPath();
        ctx.moveTo(l.x, l.y);
        ctx.lineTo(ex, ey);
        ctx.strokeStyle = grad;
        ctx.globalAlpha = l.opacity;
        ctx.lineWidth = l.width;
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      animId = requestAnimationFrame(draw);
    };

    if (isVisible) draw();

    return () => {
      if (animId) cancelAnimationFrame(animId);
      animId = null;
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onTouch);
    };
  }, [actualDensity]); // Only re-run when density changes - all other vars are local to effect

  // Low-end: don't even render the canvas
  if (isLowEnd) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};
